import { QUEUE_COUNTS_KEY, fetchQueueCounts } from '@/lib/queueCounts';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/keys';
import { LIVE } from './queryClient';
import { useSession } from './session';
import type { NavBadges } from '@/components/nav';

/**
 * The sidebar count badges. Every one of them is a number some other screen also shows, so
 * they are read from the same endpoints those screens read and never recomputed here.
 *
 * A query the role cannot make is disabled rather than fired and rejected, because a 403 an
 * operator did not ask for is noise in the audit log.
 */
export interface InboxCounts {
  crit: number;
  warn: number;
  info: number;
  open: number;
  assigned: number;
  snoozed: number;
  total: number;
}

/**
 * The one open count. /inbox/counts counts rows in the database; the list endpoint
 * returns a page, and a page is not a count, so reading `items.length` made the sidebar
 * report its own page size. Both the sidebar badge and the Action Inbox header read this.
 */
export function useInboxCounts() {
  const { can } = useSession();
  return useQuery({
    queryKey: qk.inbox({ counts: true }),
    queryFn: () => api.get<InboxCounts>('/inbox/counts'),
    enabled: can('inbox.read'),
    ...LIVE,
  });
}

/** Live items waiting on someone: open plus assigned plus snoozed, as the API counts them. */
export function useInboxOpenCount(): number {
  const counts = useInboxCounts();
  return counts.data?.total ?? 0;
}

export function useNavBadges(): NavBadges & { inboxCount: number } {
  const { can } = useSession();

  const inbox = useInboxCounts();

  // Both of these are gated on the permission the route actually declares, not on the
  // wider read_packer / read_agent family. The API asks for `orders.read` on
  // /orders/queues and `stock.read` on /stock/summary, so a packer firing either would
  // collect a 403 nobody asked for.
  const queues = useQuery({
    queryKey: QUEUE_COUNTS_KEY,
    queryFn: fetchQueueCounts,
    enabled: can('orders.read'),
    ...LIVE,
  });

  const stock = useQuery({
    queryKey: qk.metrics('stock-summary', 'point'),
    queryFn: () => api.get<{ critical: number }>('/stock/summary'),
    enabled: can('stock.read'),
    ...LIVE,
  });

  const support = useQuery({
    queryKey: qk.support('badge'),
    // The response is the queue counts plus a `handoff_open` boolean, so it is not a
    // uniform Record<string, number>. Only the count this badge shows is typed.
    queryFn: () => api.get<{ needs_reply?: number }>('/support/queues'),
    enabled: can('support.read'),
    ...LIVE,
  });

  const inboxCount = inbox.data?.total ?? 0;

  const badges: NavBadges & { inboxCount: number } = { inboxCount };
  if (inbox.data) badges.inbox = inboxCount;
  if (queues.data) badges.toPack = queues.data.to_pack;
  if (stock.data) badges.criticalSkus = stock.data.critical;
  if (support.data) badges.needsReply = support.data.needs_reply ?? 0;
  return badges;
}
